import { useRouter } from 'next/router'
import { useState } from 'react'

import { useStore } from '@nanostores/react'
import {
    Button,
    ButtonGroup,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Modal,
    ModalBody,
    ModalContent,
    Spinner,
    cn,
} from '@nextui-org/react'
import { useThrowingFn } from 'beskar/landing'
import { ChevronDownIcon, GitPullRequest, XIcon } from 'lucide-react'
import toast from 'react-hot-toast'
import { atoms } from 'website/src/lib/atoms'
import { getMarkdown, sleep } from 'website/src/lib/editor-utils'
import { useRootProps } from 'website/src/lib/hooks'
import { yDocToJson } from 'website/src/lib/utils'
import {
    createPrSuggestion,
    commitChangesToRepo,
} from 'website/src/pages/api/functions'
import { minimizeMarkdownChanges } from 'website/src/lib/editor-utils'

export function PRButton() {
    const {
        isPR,
        siteId,
        pageSlug,
        installationId,
        orgId,
        branchId,
        projectType,
        userId,
        githubOwner,
        githubRepo,
        pageMarkdown = '',
    } = useRootProps()
    const persistence = useStore(atoms.docPersistence)
    const editor = useStore(atoms.editor)
    const [githubAuthUrl, setGithubAuthUrl] = useState('')
    const { fn: openPR, isLoading } = useThrowingFn({
        async fn() {
            if (!userId) {
                return atoms.showSignupModal.set(true)
            }

            const reconstructed = getMarkdown(editor?.state.doc, pageSlug)
            const oldDoc = atoms.docWithoutAnonymousChanges.get()
            const json = yDocToJson(oldDoc)
            const oldMd = getMarkdown(json, pageSlug)
            if (reconstructed.trim() === oldMd.trim()) {
                toast.error('No changes made')
                return
            }
            const markdown = minimizeMarkdownChanges(
                pageMarkdown,
                reconstructed,
                pageSlug,
            )

            if (projectType === 'github-commit') {
                console.log('pushing changes to github')
                const { githubAuthUrl, prUrl, commitUrl } =
                    await commitChangesToRepo({
                        orgId,
                        siteId,
                        branchId,
                        content: markdown,
                        pageSlug,
                        redirectToPath: window.location.pathname,
                    })
                if (githubAuthUrl) {
                    setGithubAuthUrl(githubAuthUrl)
                    return
                }
                if (prUrl) {
                    window.location.href = prUrl
                    await sleep(2000)
                    return
                }
                if (commitUrl) {
                    window.location.href = commitUrl
                    await sleep(2000)
                    return
                }
                console.error('no url from commitChangesToRepo')
                return
            }
            console.log('pushing changes to pr')
            const { githubAuthUrl, prUrl } = await createPrSuggestion({
                orgId,
                installationId,
                siteId,
                branchId,
                content: markdown,
                pageSlug,
                projectType,
                redirectToPath: window.location.pathname,
            })

            if (githubAuthUrl) {
                setGithubAuthUrl(githubAuthUrl)
                return
            }
            if (prUrl) {
                window.location.href = prUrl
                await sleep(2000)
                return
            }
            // window.location.href = pr.url || '/'
        },
    })
    const router = useRouter()
    const docPersistence = useStore(atoms.docPersistence)
    const { fn: discard, isLoading: isDiscarding } = useThrowingFn({
        async fn() {
            docPersistence?.emit('cleared', [])
            docPersistence?.clearData()

            await router.replace({
                query: {
                    ...router.query,
                    k: Number(router.query.k || 0) + 1,
                },
            })
        },
    })
    let localChanges = useStore(atoms.localChangesForPR)
    if (!isPR) {
        return null
    }

    const cls = 'text-white bg-purple-400 dark:bg-purple-300'
    return (
        <>
            <ButtonGroup>
                <Button
                    // variant='ghost'
                    startContent={
                        !isLoading && <GitPullRequest className='w-4' />
                    }
                    className={cn(
                        'border-0 tracking-tight pr-2 font-semibold',
                        cls,
                    )}
                    aria-label={
                        projectType === 'github-commit'
                            ? 'create commit'
                            : 'create pr'
                    }
                    // size='sm'
                    // color='indigo'
                    isLoading={isLoading}
                    onClick={openPR}
                >
                    {projectType === 'github-commit'
                        ? 'Push Changes to GitHub'
                        : 'Open PR for Current File'}
                </Button>
                <Dropdown backdrop='opaque' placement='bottom-end'>
                    <DropdownTrigger>
                        <Button
                            // size='sm'
                            className={cn('border-0 ', cls)}
                            isIconOnly
                        >
                            <ChevronDownIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownMenu
                        aria-label='Merge options'
                        // selectionMode='single'
                        className='max-w-[300px]'
                    >
                        <DropdownItem
                            onClick={discard}
                            // closeOnSelect={false}
                            startContent={
                                !isDiscarding ? (
                                    <XIcon className='h-4' />
                                ) : (
                                    <Spinner className='h-4' />
                                )
                            }
                            key='merge'
                        >
                            Discard {localChanges || 'all'}{' '}
                            {localChanges === 1 ? 'change' : 'changes'}
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            </ButtonGroup>
            <Modal
                backdrop='opaque'
                size='2xl'
                className=''
                isOpen={!!githubAuthUrl}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setGithubAuthUrl('')
                    }
                }}
            >
                <ModalContent className='flex !py-12 items-center flex-col gap-4'>
                    <div className='flex flex-col text-center gap-1 text-lg font-semibold'>
                        Connect GitHub to Continue
                    </div>
                    <div className='max-w-[400px] text-center opacity-70 font-medium'>
                        {projectType === 'github-commit'
                            ? `After connecting Github Holocron will update the PR with your changes`
                            : `After connecting Github Holocron will create a PR to ${githubOwner}/${githubRepo} with your changes`}
                    </div>

                    <ModalBody>
                        <a href={githubAuthUrl}>
                            <Button
                                size='lg'
                                className='font-semibold text-lg mt-2'
                                startContent={<MdiGithub className='w-6' />}
                            >
                                Connect GitHub
                            </Button>
                        </a>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    )
}

function MdiGithub(props) {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' {...props}>
            <path
                fill='currentColor'
                d='M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5c.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34c-.46-1.16-1.11-1.47-1.11-1.47c-.91-.62.07-.6.07-.6c1 .07 1.53 1.03 1.53 1.03c.87 1.52 2.34 1.07 2.91.83c.09-.65.35-1.09.63-1.34c-2.22-.25-4.55-1.11-4.55-4.92c0-1.11.38-2 1.03-2.71c-.1-.25-.45-1.29.1-2.64c0 0 .84-.27 2.75 1.02c.79-.22 1.65-.33 2.5-.33c.85 0 1.71.11 2.5.33c1.91-1.29 2.75-1.02 2.75-1.02c.55 1.35.2 2.39.1 2.64c.65.71 1.03 1.6 1.03 2.71c0 3.82-2.34 4.66-4.57 4.91c.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2Z'
            ></path>
        </svg>
    )
}
